import { PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Spin,
} from 'antd';
import SelectContactTable from 'components/Contacts/SelectContactTable';
import FormLegend from 'components/Forms/FormLegend';
import AddAssetModal from 'components/Modals/AddAssetModal';
import PurchaseOrderAddModal from 'components/PurchaseOrders/PurchaseOrderAddModal';
import WarrantiesTable from 'components/Warranties/WarrantiesTable';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { customFleets } from 'shared';
import { AppStore, ContactStore, PurchaseOrderStore, ShopStore } from 'stores';
import CarStore from 'stores/CarStore';
import { Logger } from 'stores/Classes';
import {
  WorkOrderRepairPriorityEnum,
  WorkOrderStatusEnum,
  WorkOrderTypeOfServiceEnum,
} from 'stores/Classes/WorkOrderObject';
import CurrentUserStore from 'stores/CurrentUserStore';
import WarrantyStore from 'stores/WarrantyStore';

const { Option } = Select;

const fullWidth = {
  width: '100%',
};

const WorkOrderDetailsFormDesktop = forwardRef(
  (
    { shopId, onCarSelect, onGetCarDetails, workOrder, shopUsers, isDisabled },
    ref
  ) => {
    const [form] = Form.useForm();

    useImperativeHandle(ref, () => ({
      form,
    }));

    const [state, setState] = useState({
      cars: [],
      allCars: [],
      loadingCarData: false,
      selectedCar: undefined,
      selectedCarFromForm: undefined,
      searchingCar: false,
      assetTypeDataSource: ['Vehicle', 'Trailer', 'Other'],
      addAssetModalVisible: false,
      addPOModalVisible: false,
      loadingAssetWarranties: false,
      warrantyFilters: {},
      warrantyPagination: {},
      warrantySorter: {},
      assetWarranties: [],
      vendors: [],
      purchaseOrders: [],
      preSelectedAssets: [],
    });

    useEffect(() => {
      const init = async () => {
        await initAssets();
        loadShopVendors();
        loadShopPurchaseOrders();
      };

      init();

      if (workOrder?.woSelectVehicle) {
        handleInitialVehicle();
      }
    }, []);

    const handleInitialVehicle = async () => {
      setState((prev) => ({
        ...prev,
        searchingCar: true,
        loadingCarData: true,
      }));

      const car = state.allCars.find(
        (car) => car.id === workOrder.woSelectVehicle
      );
      if (onGetCarDetails) {
        onGetCarDetails(car);
      }

      if (car) {
        await form.setFieldValue('woAssetType', _.capitalize(car.carType));
      }

      setState((prev) => ({
        ...prev,
        selectedCar: workOrder.woSelectVehicle,
        searchingCar: false,
        loadingCarData: false,
      }));

      onChangeFormCar(workOrder.woSelectVehicle);
    };

    const loadShopVendors = async () => {
      try {
        if (
          !ShopStore.currentShop.id ||
          ShopStore.currentShop.id === '-1' ||
          ShopStore.currentShop.id === -1
        ) {
          setState((prev) => ({ ...prev, vendors: [] }));
          return;
        }
        const vendors = await ContactStore.getShopContacts(
          ShopStore.currentShop.id
        );
        setState((prev) => ({ ...prev, vendors: vendors || [] }));
      } catch (error) {
        Logger.error(error);
      }
    };

    const loadShopPurchaseOrders = async () => {
      try {
        if (
          !ShopStore.currentShop.id ||
          ShopStore.currentShop.id === '-1' ||
          ShopStore.currentShop.id === -1
        ) {
          setState((prev) => ({ ...prev, purchaseOrders: [] }));
          return;
        }
        const purchaseOrders = await PurchaseOrderStore.fetchShopPurchaseOrders(
          ShopStore.currentShop.id
        );
        setState((prev) => ({ ...prev, purchaseOrders: purchaseOrders || [] }));
      } catch (error) {
        Logger.error(error);
      }
    };

    const loadAssetWarranties = async (carId) => {
      try {
        setState((prev) => ({
          ...prev,
          loadingAssetWarranties: true,
          assetWarranties: [],
        }));

        const filters = {
          ...state.warrantyFilters,
          carIds: carId,
        };

        if (filters.status?.length) {
          filters.statuses = filters.status.join(',');
        }

        if (state.warrantyPagination.current) {
          const page = state.warrantyPagination.current;
          filters.limit = state.warrantyPagination.pageSize;
          filters.offset = (page - 1) * filters.limit;
        }

        if (state.warrantySorter.column && state.warrantySorter.order) {
          const field = state.warrantySorter.field;
          const direction = state.warrantySorter.order === 'ascend' ? '' : '-';
          filters.sortBy = `${direction}${field}`;
        }

        const { data, meta } = await WarrantyStore.getWarranties(
          ShopStore.currentShop.id,
          filters
        );

        setState((prev) => ({
          ...prev,
          assetWarranties: data,
          warrantyPagination: {
            ...prev.warrantyPagination,
            total: meta.total,
          },
        }));
      } catch (error) {
        Logger.error(error.toString());
      } finally {
        setState((prev) => ({ ...prev, loadingAssetWarranties: false }));
      }
    };

    const getCreatedByOptions = () => {
      return shopUsers.map((user, index) => (
        <Option key={index} value={user.id}>
          {user.name}
        </Option>
      ));
    };

    const getAssignedToOptions = () => {
      return shopUsers.map((user, index) => (
        <Option key={index} value={user.id}>
          {user.name}
        </Option>
      ));
    };

    const getAssetOptions = () => {
      const filteredCarsBasedOnAssetType = state.cars.filter(
        (car) =>
          (car?.carType || '').toLowerCase() ===
          (form.getFieldValue('woAssetType') || '').toLowerCase()
      );

      return filteredCarsBasedOnAssetType.map((car, index) => {
        const vinInfo = car?.carType !== 'other' ? ` - ${car?.vin}` : '';
        return (
          <Option key={index} value={car?.id}>
            {`${car?.carName || car?.unitId || car?.make + ' ' + car?.model + ' ' + car?.year}${vinInfo}`}
          </Option>
        );
      });
    };

    const initAssets = async () => {
      setState((prev) => ({
        ...prev,
        searchingCar: true,
        loadingCarData: true,
      }));
      const assets = await getShopCars();
      setState((prev) => ({
        ...prev,
        allCars: assets?.length ? [...assets] : [],
        cars: assets?.length ? [...assets] : [],
        selectedCar: undefined,
        searchingCar: false,
        loadingCarData: false,
      }));
    };

    const getShopCars = async () => {
      return CarStore.getShopCarsBasicInfoAndReturn(ShopStore.currentShop.id);
    };

    const searchForCars = async (search) => {
      setState((prev) => ({ ...prev, searchingCar: true }));
      const cars = await state.allCars;
      const filteredCars = cars.filter((car) => {
        return (
          (car?.carName || '').toLowerCase().includes(search.toLowerCase()) ||
          (car?.unitId || '').toLowerCase().includes(search.toLowerCase()) ||
          (car?.vin || '').toLowerCase().includes(search.toLowerCase())
        );
      });
      setState((prev) => ({
        ...prev,
        cars: filteredCars,
        searchingCar: false,
      }));
    };

    const onCarSearch = async (value) => {
      if (!value) {
        return setState((prev) => ({ ...prev, cars: state.allCars }));
      }
      searchForCars(value);
    };

    const onCarSearchChange = (selectedCar) => {
      setState((prev) => ({ ...prev, selectedCar }));
      if (onCarSelect) {
        onCarSelect(selectedCar);
      }
      if (onGetCarDetails) {
        const car = state.allCars.find((car) => car.id === selectedCar);
        onGetCarDetails(car);
      }
    };

    const onChangeFormCar = (value) => {
      setState((prev) => ({
        ...prev,
        selectedCarFromForm: value,
        preSelectedAssets: [value],
      }));
    };

    useEffect(() => {
      if (state.selectedCarFromForm) {
        loadAssetWarranties(state.selectedCarFromForm);
        if (onCarSelect) {
          onCarSelect(state.selectedCarFromForm);
        }
      }
    }, [state.selectedCarFromForm]);

    const getCurrentLoggedUser = () => {
      return CurrentUserStore.user.id;
    };

    const onAddAssets = async (newAssets) => {
      setState((prev) => ({ ...prev, addAssetModalVisible: false }));

      if (newAssets.length) {
        try {
          for (const asset of newAssets) {
            await CarStore.createGenericAsset({
              vin: asset.vin,
              source: 'user',
              assetType: asset.assetType,
              shopId: ShopStore.currentShop.id,
              licensePlate: '',
              baseMileage: asset.mileage || 0,
              unitId: asset.unitId,
              engineHours: asset.engineHours || 0,
            });
          }
          AppStore.addSuccess('Asset added successfully');
          await initAssets();
        } catch {
          AppStore.addError('Error adding assets');
        }
      }
    };

    const onAddPurchaseOrder = async (newPurchaseOrder) => {
      setState((prev) => ({ ...prev, addPOModalVisible: false }));

      if (newPurchaseOrder) {
        try {
          const newPO =
            await PurchaseOrderStore.savePurchaseOrder(newPurchaseOrder);
          AppStore.addSuccess('Purchase Order added successfully');
          await loadShopPurchaseOrders();
          form.setFieldValue('purchase_order_id', newPO.id);
        } catch {
          AppStore.addError('Error adding Purchase Order');
        }
      }
    };

    const getStatusOptions = () => {
      return Object.keys(WorkOrderStatusEnum)
        .map((key) => ({
          key,
          value: WorkOrderStatusEnum[key],
        }))
        .map((status) => (
          <Option key={status.key} value={status.key}>
            {status.value}
          </Option>
        ));
    };

    const getPriorityOptions = () => {
      return Object.keys(WorkOrderRepairPriorityEnum)
        .map((key) => ({
          key,
          value: WorkOrderRepairPriorityEnum[key],
        }))
        .map((priority) => (
          <Option key={priority.key} value={priority.key}>
            {priority.value}
          </Option>
        ));
    };

    const getTypeOptions = () => {
      return Object.keys(WorkOrderTypeOfServiceEnum)
        .map((key) => ({
          key,
          value: WorkOrderTypeOfServiceEnum[key],
        }))
        .map((type) => (
          <Option key={type.key} value={type.key}>
            {type.value}
          </Option>
        ));
    };

    const filterOption = (input, option) => {
      return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    };

    if (state.loadingCarData) {
      return <Spin />;
    }

    return (
      <Form
        form={form}
        initialValues={{
          woInvoiceNumber: workOrder.woInvoiceNumber,
          woCreatedBy: workOrder.woCreatedBy || getCurrentLoggedUser(),
          woAssignedTo: workOrder.woAssignedTo,
          woAssetType: workOrder.woAssetType,
          woSelectVehicle: workOrder.woSelectVehicle,
          woStatus: workOrder.woStatus,
          woRepairType: workOrder.woRepairType,
          woRepairPriority: workOrder.woRepairPriority,
          woIssueReportDate: workOrder.woIssueReportDate,
          woStartDate: workOrder.woStartDate,
          woCompletedDate: workOrder.woCompletedDate,
          mileage: workOrder.mileage,
          engineHours: workOrder.engineHours,
          additionalNotes: workOrder.additionalNotes,
          purchase_order_id: workOrder.purchase_order_id,
          vendor_id: workOrder.vendor_id,
          driver_name: workOrder.driver_name,
          driver_number: workOrder.driver_number,
          driver_location: workOrder.driver_location,
          time_of_event: workOrder.time_of_event,
          vehicle_weight: workOrder.vehicle_weight,
          driver_notes: workOrder.driver_notes,
          billing_through: workOrder.billing_through,
        }}
      >
        <FormLegend>Work Order Details</FormLegend>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="Work order number:"
              name="woInvoiceNumber"
              rules={[
                { required: true, message: 'Work order number is required!' },
              ]}
            >
              <Input
                prefix="#"
                allowClear
                placeholder="#000000"
                disabled={isDisabled}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              label="Created by:"
              name="woCreatedBy"
              rules={[{ required: true, message: 'Created by is required!' }]}
            >
              <Select
                style={fullWidth}
                placeholder="Select a User"
                showSearch
                optionFilterProp="children"
                filterOption={filterOption}
                disabled={isDisabled}
                data-testid="wo-created-by-select"
              >
                {getCreatedByOptions()}
              </Select>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              label="Assigned To:"
              name="woAssignedTo"
              rules={[{ required: true, message: 'Assigned to is required!' }]}
            >
              <Select
                style={fullWidth}
                placeholder="Select a User"
                showSearch
                optionFilterProp="children"
                filterOption={filterOption}
                disabled={isDisabled}
                data-testid="wo-assigned-to-select"
              >
                {getAssignedToOptions()}
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label="Asset Type:" name="woAssetType">
              <Select
                style={fullWidth}
                placeholder="Asset Type"
                disabled={isDisabled}
                onChange={() => form.setFieldValue('woSelectVehicle', null)}
                data-testid="wo-asset-type-select"
              >
                <Option value="Vehicle">Vehicle</Option>
                <Option value="Trailer">Trailer</Option>
                <Option value="Other">Other</Option>
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            {state.loadingCarData ? (
              <Spin />
            ) : (
              <>
                <Form.Item
                  label="Select Asset or Miscellaneous Equipment:"
                  name="woSelectVehicle"
                  rules={[{ required: true, message: 'Asset is required!' }]}
                  style={{ marginBottom: 3 }}
                >
                  <Select
                    style={fullWidth}
                    onChange={onCarSearchChange}
                    onSearch={onCarSearch}
                    placeholder="Search by Unit ID"
                    showSearch
                    optionFilterProp="children"
                    filterOption={filterOption}
                    loading={state.searchingCar}
                    disabled={isDisabled}
                    data-testid="wo-select-asset-select"
                  >
                    {getAssetOptions()}
                  </Select>
                </Form.Item>
                <Button
                  style={{ padding: 0 }}
                  type="link"
                  icon={<PlusOutlined />}
                  onClick={() =>
                    setState((prev) => ({
                      ...prev,
                      addAssetModalVisible: true,
                    }))
                  }
                >
                  Add Asset
                </Button>
              </>
            )}
          </Col>

          {customFleets.ufpFleet.includes(
            ShopStore.currentShop.parentShopId || ShopStore.currentShop.id
          ) && (
            <Col span={24}>
              <Form.Item
                label="Purchase Order:"
                name="purchase_order_id"
                style={{ marginBottom: 3 }}
              >
                <Select
                  style={fullWidth}
                  placeholder="Select a Purchase Order"
                  disabled={isDisabled}
                  onChange={() => form.setFieldValue('purchase_order_id', null)}
                >
                  {state.purchaseOrders.map((po) => {
                    let poName = `#${po.purchase_order_number}`;
                    if (po.reference_number) {
                      poName += ` - ${po.reference_number}`;
                    }
                    return (
                      <Option key={po.id} value={po.id}>
                        {poName}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
              <Button
                style={{ padding: 0 }}
                type="link"
                icon={<PlusOutlined />}
                onClick={() =>
                  setState((prev) => ({ ...prev, addPOModalVisible: true }))
                }
              >
                Add Purchase Order
              </Button>
            </Col>
          )}

          <Col span={8}>
            <Form.Item
              label="Status:"
              name="woStatus"
              rules={[{ required: true, message: 'Status is required!' }]}
            >
              <Select
                style={fullWidth}
                placeholder="Select a Status"
                showSearch
                optionFilterProp="children"
                filterOption={filterOption}
                disabled={isDisabled}
                data-testid="wo-status-select"
              >
                {getStatusOptions()}
              </Select>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              label="Repair Type:"
              name="woRepairType"
              rules={[{ required: true, message: 'Repair Type is required!' }]}
            >
              <Select
                style={fullWidth}
                placeholder="Select a Repair Type"
                showSearch
                optionFilterProp="children"
                filterOption={filterOption}
                disabled={isDisabled}
                data-testid="wo-repair-type-select"
              >
                {getTypeOptions()}
              </Select>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              label="Repair Priority:"
              name="woRepairPriority"
              rules={[
                { required: true, message: 'Repair Priority is required!' },
              ]}
            >
              <Select
                style={fullWidth}
                placeholder="Select a Repair Priority"
                showSearch
                optionFilterProp="children"
                filterOption={filterOption}
                disabled={isDisabled}
              >
                {getPriorityOptions()}
              </Select>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label="Issue report date:" name="woIssueReportDate">
              <DatePicker
                format="MM/DD/YYYY HH:mm:ss"
                style={fullWidth}
                showTime
                placeholder="Select date and time"
                disabled={isDisabled}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label="Work Order Start:" name="woStartDate">
              <DatePicker
                format="MM/DD/YYYY HH:mm:ss"
                style={fullWidth}
                showTime
                placeholder="Select date and time"
                disabled={isDisabled}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label="Work Order Completed:" name="woCompletedDate">
              <DatePicker
                style={fullWidth}
                showTime
                placeholder="Select date and time"
                disabled={isDisabled}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label="Mileage:" name="mileage">
              <InputNumber
                style={fullWidth}
                placeholder="Mileage"
                disabled={isDisabled}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label="Engine Hours:" name="engineHours">
              <InputNumber
                style={fullWidth}
                placeholder="Engine Hours"
                disabled={isDisabled}
              />
            </Form.Item>
          </Col>

          {customFleets.ufpFleet.includes(
            ShopStore.currentShop.parentShopId || ShopStore.currentShop.id
          ) && (
            <Col span={24}>
              <Form.Item label="Additional Notes:" name="additionalNotes">
                <Input.TextArea
                  style={fullWidth}
                  placeholder="Additional Notes"
                  disabled={isDisabled}
                  rows={6}
                />
              </Form.Item>
            </Col>
          )}

          <Col span={24}>
            <div style={{ marginTop: 15, marginBottom: 15 }}>
              <FormLegend>Warranties on this Asset</FormLegend>
              <WarrantiesTable
                isLoading={state.loadingAssetWarranties}
                data={state.assetWarranties}
                assets={state.allCars}
                vendors={state.vendors}
                preSelectedAssets={state.preSelectedAssets}
                onUpdate={() => loadAssetWarranties(state.selectedCarFromForm)}
                onChangePagination={({ pagination, filters, sorter }) => {
                  setState(
                    (prev) => ({
                      ...prev,
                      warrantyPagination: pagination,
                      warrantyFilters: filters,
                      warrantySorter: sorter,
                    }),
                    () => {
                      loadAssetWarranties(state.selectedCarFromForm);
                    }
                  );
                }}
              />
            </div>
          </Col>
        </Row>

        {customFleets.ufpFleet.includes(
          ShopStore.currentShop.parentShopId || ShopStore.currentShop.id
        ) && (
          <Row gutter={16}>
            <Col span={24}>
              <FormLegend>Driver Report Details</FormLegend>
            </Col>
            <Col span={8}>
              <Form.Item label="Driver Name" name="driver_name">
                <Input style={fullWidth} disabled={isDisabled} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Driver Number" name="driver_number">
                <Input style={fullWidth} disabled={isDisabled} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Location" name="driver_location">
                <Input style={fullWidth} disabled={isDisabled} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Time of Event" name="time_of_event">
                <DatePicker
                  format="MM/DD/YYYY HH:mm:ss"
                  style={fullWidth}
                  showTime
                  placeholder="Select date and time"
                  disabled={isDisabled}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Weight of Vehicle" name="vehicle_weight">
                <Input style={fullWidth} disabled={isDisabled} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Driver Notes" name="driver_notes">
                <Input style={fullWidth} disabled={isDisabled} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Billing Through" name="billing_through">
                <Select style={fullWidth} disabled={isDisabled}>
                  <Option value={1}>
                    National account, reference the DR# 0372704
                  </Option>
                  <Option value={2}>
                    National Account, Bridgestone (account number 956315)
                  </Option>
                  <Option value={3}>
                    National Accounts - Bridgestone (Account #957128)
                  </Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        )}

        <Row gutter={16}>
          <Col span={24}>
            <FormLegend>Vendor Details</FormLegend>
          </Col>
          <Col span={24}>
            {customFleets.ufpFleet.includes(
              ShopStore.currentShop.parentShopId || ShopStore.currentShop.id
            ) && (
              <SelectContactTable
                contacts={state.vendors}
                onSelectChange={(selectedRowKeys) => {
                  form.setFieldValue('vendor_id', selectedRowKeys[0]);
                }}
              />
            )}
            <Form.Item
              style={{
                visibility: customFleets.ufpFleet.includes(
                  ShopStore.currentShop.parentShopId || ShopStore.currentShop.id
                )
                  ? 'hidden'
                  : 'visible',
                height: customFleets.ufpFleet.includes(
                  ShopStore.currentShop.parentShopId || ShopStore.currentShop.id
                )
                  ? 0
                  : 'auto',
              }}
              label="Vendor:"
              name="vendor_id"
            >
              <Select
                style={fullWidth}
                placeholder="Select a Vendor"
                disabled={isDisabled}
                onChange={() => form.setFieldValue('vendor_id', null)}
              >
                {state.vendors.map((vendor) => (
                  <Option key={vendor.id} value={vendor.id}>
                    {vendor.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <AddAssetModal
          visible={state.addAssetModalVisible}
          hideVin={true}
          onOk={onAddAssets}
          onCancel={() =>
            setState((prev) => ({ ...prev, addAssetModalVisible: false }))
          }
        />

        <PurchaseOrderAddModal
          visible={state.addPOModalVisible}
          onSave={onAddPurchaseOrder}
          onCancel={() =>
            setState((prev) => ({ ...prev, addPOModalVisible: false }))
          }
        />
      </Form>
    );
  }
);

WorkOrderDetailsFormDesktop.propTypes = {
  shopId: PropTypes.number,
  onCarSelect: PropTypes.func,
  onGetCarDetails: PropTypes.func,
  workOrder: PropTypes.object,
  shopUsers: PropTypes.array,
  isDisabled: PropTypes.bool,
};

WorkOrderDetailsFormDesktop.defaultProps = {
  workOrder: {},
};

export default WorkOrderDetailsFormDesktop;
